import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { Panel } from "@/components/common/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Search, BookOpen, FileText, Sparkles, Trash2, Library } from "lucide-react";
import { AnimationFrame } from "@/components/common/AnimationFrame";
import { researchApi, errMsg } from "@/lib/research";
import { ResearchScope } from "@/components/research/ResearchScope";
import { AuthorityCard } from "@/components/research/AuthorityCard";
import { AddAuthorityDialog } from "@/components/research/AddAuthorityDialog";
import { CitationVerifier, ExternalLinks } from "@/components/research/CitationVerifier";
import { PropositionDetail } from "@/components/research/PropositionDetail";
import { VerificationBadge } from "@/components/research/AuthorityCard";
import { MatterChip } from "@/components/common/MatterSelector";

export default function Research() {
  const { db } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [scope, setScope] = useState(() => ({ jurisdiction: "Any", matterId: new URLSearchParams(window.location.search).get("matterId") || null, contrary: false }));
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [auths, setAuths] = useState([]);
  const [props, setProps] = useState([]);
  const [propId, setPropId] = useState(null);

  const reload = () => Promise.all([researchApi.authorities(), researchApi.propositions()]).then(([a, p]) => { setAuths(a); setProps(p); }).catch(() => {});
  useEffect(() => { reload(); }, []);

  const search = async () => {
    if (!q.trim()) return;
    setLoading(true);
    try { setResults(await researchApi.search({ query: q, ...scope })); } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  const onChange = (a) => { setAuths((v) => a.deleted ? v.filter((x) => x.id !== a.id) : v.map((x) => x.id === a.id ? a : x)); if (results) setResults((r) => ({ ...r, authorities: a.deleted ? r.authorities.filter((x) => x.id !== a.id) : r.authorities.map((x) => x.id === a.id ? a : x) })); researchApi.propositions().then(setProps).catch(() => {}); };
  const askAI = () => { sessionStorage.setItem("nyk_research_seed", `${scope.contrary ? "Find contrary authority on: " : ""}${q}`); sessionStorage.setItem("nyk_research_scope", JSON.stringify(scope)); navigate("/ai-search"); };

  return (
    <div>
      <PageHeader title="Legal Research" subtitle="Search your authority library, uploaded judgments and matter records. External databases are link-out only until authorised access is configured.">
        <AddAuthorityDialog onCreated={(a) => setAuths((v) => [a, ...v.filter((x) => x.id !== a.id)])} />
      </PageHeader>

      <Panel className="p-4">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-muted" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search propositions, citations, uploaded judgments or matter records…" className="pl-9" data-testid="research-input" />
          </div>
          <Button onClick={search} disabled={loading} data-testid="research-search-btn">{loading ? "Searching…" : "Search"}</Button>
          <Button variant="outline" onClick={askAI} disabled={!q.trim()} data-testid="research-ask-ai"><Sparkles className="mr-1 h-4 w-4 text-copper" /> Ask AI</Button>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3"><MatterChip matterId={scope.matterId} label="Linked Matter" onChange={(mm) => setScope((s) => ({ ...s, matterId: mm.id }))} /><ResearchScope scope={scope} onChange={setScope} /></div>
      </Panel>

      {loading && <Panel className="p-4"><AnimationFrame slug="precedent-graph" title="Searching the precedent network" height={240} maxWidth={420} /></Panel>}

      {results && (
        <div className="mt-6 space-y-4" data-testid="research-results">
          <div className="flex items-center justify-between">
            <h3 className="font-display text-xl font-semibold text-ink">{results.authorities.length + results.documents.length + results.matterRecords.length} internal result(s) for “{results.query}”{results.contrary && <span className="ml-2 rounded-full bg-danger/10 px-2 py-0.5 text-xs text-danger">contrary only</span>}</h3>
            <Button variant="ghost" size="sm" onClick={() => setResults(null)} data-testid="research-clear">Clear</Button>
          </div>
          <Section icon={BookOpen} title={`Authorities (${results.authorities.length})`} empty="No matching authority in your library.">
            {results.authorities.map((a) => <AuthorityCard key={a.id} a={a} onChange={onChange} matterId={scope.matterId} />)}
          </Section>
          <Section icon={FileText} title={`Uploaded documents (${results.documents.length})`} empty="No uploaded document text matched. Run OCR on scanned judgments to make them searchable.">
            {results.documents.map((d) => (
              <button key={d.id} onClick={() => navigate(`/matters/${d.matterId}?tab=Documents`)} className="w-full rounded-xl border border-line bg-card p-3 text-left hover:border-copper/40" data-testid={`doc-hit-${d.id}`}>
                <div className="flex items-center justify-between"><span className="font-medium text-ink">{d.name}</span><span className="text-[10px] text-ink-muted">{d.matterId} · {d.category}{d.ocrStatus === "PARTIAL" ? " · partial OCR" : ""}</span></div>
                {d.snippet && <p className="mt-1 text-xs text-ink-2">{d.snippet}</p>}
              </button>
            ))}
          </Section>
          {scope.matterId && <Section icon={Library} title={`Matter records (${results.matterRecords.length})`} empty="No notes, hearings or orders in this matter matched.">
            {results.matterRecords.map((r) => <div key={r.id} className="rounded-xl border border-line bg-card p-3 text-xs" data-testid={`record-hit-${r.id}`}><span className="mr-1 rounded bg-muted px-1.5 py-0.5 text-[10px]">{r.type}</span>{r.text}</div>)}
          </Section>}
          <Panel className="p-3"><div className="mb-1 text-xs font-semibold text-ink">External databases — open search in a new tab (not connected, results are not imported)</div><ExternalLinks items={results.external} /></Panel>
        </div>
      )}

      {!results && (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Tabs defaultValue="library">
              <TabsList><TabsTrigger value="library" data-testid="tab-library">Authority library ({auths.length})</TabsTrigger><TabsTrigger value="contrary" data-testid="tab-contrary">Contrary ({auths.filter((a) => a.stance === "CONTRARY").length})</TabsTrigger><TabsTrigger value="props" data-testid="tab-propositions">Proposition bank ({props.length})</TabsTrigger></TabsList>
              <TabsContent value="library" className="space-y-3 pt-3">
                {auths.length === 0 && <Panel className="p-6 text-center text-sm text-ink-2">No authorities yet. Register an uploaded judgment or add organisation research.</Panel>}
                {auths.map((a) => <AuthorityCard key={a.id} a={a} onChange={onChange} />)}
              </TabsContent>
              <TabsContent value="contrary" className="space-y-3 pt-3">
                {auths.filter((a) => a.stance === "CONTRARY").length === 0 && <Panel className="p-6 text-center text-sm text-ink-2">No contrary authority tracked. Mark authorities as contrary so drafts and AI research account for them.</Panel>}
                {auths.filter((a) => a.stance === "CONTRARY").map((a) => <AuthorityCard key={a.id} a={a} onChange={onChange} />)}
              </TabsContent>
              <TabsContent value="props" className="space-y-2 pt-3">
                {props.length === 0 && <Panel className="p-6 text-center text-sm text-ink-2">Save propositions from authorities to reuse them in drafts.</Panel>}
                {props.map((p) => {
                  const m = db.matters.find((x) => x.id === p.matterId);
                  return (
                    <div key={p.id} className="flex items-start justify-between gap-2 rounded-xl border border-line bg-card p-3 text-sm" data-testid={`prop-${p.id}`}>
                      <button onClick={() => setPropId(p.id)} className="min-w-0 flex-1 text-left" data-testid="prop-open"><div className="text-ink">{p.text}{p.para && <span className="ml-1 text-[11px] text-copper">¶ {p.para}</span>}</div><div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-ink-muted"><VerificationBadge status={p.verification === "VERIFIED" ? "VERIFIED_FROM_SOURCE" : "NEEDS_VERIFICATION"} /><span>{m ? m.caseNumber : "Firm-wide"}</span>{p.issue && <span>· {p.issue}</span>}<span>· {p.authorityIds.map((id) => auths.find((a) => a.id === id)?.title).filter(Boolean).join("; ") || "no authority linked"}</span>{p.sourceType && <span className="rounded bg-muted px-1">{p.sourceType.replaceAll("_", " ").toLowerCase()}</span>}</div></button>
                      <button onClick={() => researchApi.deleteProposition(p.id).then(() => setProps((v) => v.filter((x) => x.id !== p.id)))} className="rounded p-1 text-ink-muted hover:text-danger" data-testid="prop-delete"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  );
                })}
              </TabsContent>
            </Tabs>
          </div>
          <div className="space-y-3">
            <CitationVerifier />
            <Panel className="p-4">
              <h3 className="mb-2 font-display text-lg font-semibold text-ink">Pinned</h3>
              {auths.filter((a) => a.pinned).length === 0 ? <p className="text-sm text-ink-2">No pinned authorities.</p> : auths.filter((a) => a.pinned).map((a) => <div key={a.id} className="mb-2"><AuthorityCard a={a} compact onChange={onChange} /></div>)}
            </Panel>
          </div>
        </div>
      )}
      <PropositionDetail id={propId} onClose={() => setPropId(null)} onChanged={(r) => setProps((v) => v.map((x) => x.id === r.id ? { ...x, ...r } : x))} />
    </div>
  );
}

function Section({ icon: Icon, title, empty, children }) {
  const items = React.Children.toArray(children);
  return (
    <div>
      <div className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-ink-muted"><Icon className="h-3.5 w-3.5" /> {title}</div>
      {items.length === 0 ? <p className="text-xs text-ink-muted">{empty}</p> : <div className="space-y-2">{children}</div>}
    </div>
  );
}
